import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import useBodyOverflow from 'hooks/useBodyOverflow';
import useScreenSize from 'hooks/useScreenSize';

import styles from './BottomSheet.module.scss';

const CLOSE_ANIMATION_DURATION = 600;
const DRAG_CLOSE_THRESHOLD = 80;

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({ isOpen, onClose, children, className }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const { isMobile } = useScreenSize();
  const startYRef = useRef(0);
  const dragOffsetRef = useRef(0);
  const isDraggingRef = useRef(false);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  useBodyOverflow(shouldRender);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(true);
      return;
    }

    if (!shouldRender) return undefined;

    setIsAnimating(false);
    setIsDragging(false);
    setDragOffset(0);
    dragOffsetRef.current = 0;
    isDraggingRef.current = false;
    const timer = setTimeout(() => setShouldRender(false), CLOSE_ANIMATION_DURATION);
    return () => clearTimeout(timer);
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const startDrag = (clientY: number) => {
    if (!isMobile) return;
    startYRef.current = clientY;
    isDraggingRef.current = true;
    setIsDragging(true);
  };

  const moveDrag = (clientY: number) => {
    if (!isDraggingRef.current) return;
    const offset = Math.max(0, clientY - startYRef.current);
    dragOffsetRef.current = offset;
    setDragOffset(offset);
  };

  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;
    setIsDragging(false);

    if (dragOffsetRef.current > DRAG_CLOSE_THRESHOLD) {
      onCloseRef.current();
    }

    dragOffsetRef.current = 0;
    setDragOffset(0);
  }, []);

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (event: MouseEvent) => moveDrag(event.clientY);
    const handleMouseUp = () => endDrag();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, endDrag]);

  if (!shouldRender) return null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  const sheetStyle = isDragging || dragOffset > 0 ? { transform: `translateY(${dragOffset}px)` } : undefined;

  return createPortal(
    <div className={clsx(styles.overlay, { [styles.visible]: isAnimating })} onClick={onClose} role="presentation">
      <div
        className={clsx(styles.sheet, className, { [styles.open]: isAnimating, [styles.dragging]: isDragging })}
        style={sheetStyle}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {isMobile && (
          <div
            className={styles.handleArea}
            onTouchStart={(event) => startDrag(event.touches[0].clientY)}
            onTouchMove={(event) => moveDrag(event.touches[0].clientY)}
            onTouchEnd={endDrag}
            onMouseDown={(event) => startDrag(event.clientY)}
          >
            <div className={styles.handle} />
          </div>
        )}
        <div className={styles.content}>{children}</div>
      </div>
    </div>,
    modalRoot,
  );
};

export default BottomSheet;
